import { AUDIO } from '@/utils/constants';
import { audioCache } from '@/services/cache/audioCache';
import { getAutoCachePlayback } from '@/utils/preferences';

export class AudioPlayerService {
  private audio: HTMLAudioElement;
  private currentUrl: string | null = null;
  private currentBlobUrl: string | null = null;
  private cachingKeys = new Set<string>();
  private pendingPlayingHandler: (() => void) | null = null;

  constructor() {
    this.audio = new Audio();
    this.audio.volume = AUDIO.DEFAULT_VOLUME;
  }

  async loadAudio(url: string): Promise<void> {
    // Use base URL (without query params) as cache key since presigned URLs change
    const cacheKey = url.split('?')[0];

    // Cancel any background-cache that was waiting on a previous track.
    this.clearPendingCache();

    // Clean up previous blob URL if it exists
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }

    // Use the fully cached blob when available (instant, works offline).
    const cachedBlob = await audioCache.get(cacheKey);
    if (cachedBlob) {
      const blobUrl = URL.createObjectURL(cachedBlob);
      this.currentBlobUrl = blobUrl;
      this.audio.pause();
      this.audio.src = blobUrl;
      this.currentUrl = url;
      return this.waitForReady();
    }

    // Not cached: stream straight from the network so playback starts as soon
    // as enough has buffered. When auto-cache is enabled (opt-in), also download
    // a full copy to the offline cache — deferred until playback has actually
    // started so the full-file fetch doesn't compete with the initial buffer
    // (faster time-to-first-sound, especially in the Android WebView).
    this.audio.pause();
    this.audio.src = url;
    this.currentUrl = url;
    if (getAutoCachePlayback()) {
      this.scheduleBackgroundCache(url, cacheKey);
    }
    return this.waitForReady();
  }

  /**
   * Start the background full-file download only once playback has begun, after
   * a short grace period so the player can build its initial buffer first.
   */
  private scheduleBackgroundCache(url: string, cacheKey: string): void {
    const onPlaying = () => {
      this.clearPendingCache();
      setTimeout(() => {
        // Only cache if this is still the active track.
        if (this.currentUrl === url) {
          this.cacheInBackground(url, cacheKey);
        }
      }, 2000);
    };
    this.pendingPlayingHandler = onPlaying;
    this.audio.addEventListener('playing', onPlaying, { once: true });
  }

  private clearPendingCache(): void {
    if (this.pendingPlayingHandler) {
      this.audio.removeEventListener('playing', this.pendingPlayingHandler);
      this.pendingPlayingHandler = null;
    }
  }

  private waitForReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = (e: Event) => {
        cleanup();
        reject(e);
      };
      const cleanup = () => {
        this.audio.removeEventListener('canplay', onReady);
        this.audio.removeEventListener('error', onError);
      };
      this.audio.addEventListener('canplay', onReady, { once: true });
      this.audio.addEventListener('error', onError, { once: true });
      this.audio.load();
    });
  }

  private cacheInBackground(url: string, cacheKey: string): void {
    if (this.cachingKeys.has(cacheKey)) return;
    this.cachingKeys.add(cacheKey);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.statusText}`);
        }
        return response.blob();
      })
      // Cache with 6 month expiration: 6 * 30 * 24 = 4320 hours
      .then((blob) => audioCache.set(cacheKey, blob, 4320))
      .catch((error) => console.error('[AudioPlayer] Background cache failed:', error))
      .finally(() => this.cachingKeys.delete(cacheKey));
  }

  async play(): Promise<void> {
    try {
      await this.audio.play();
    } catch (error) {
      console.error('Playback failed:', error);
      throw error;
    }
  }

  pause(): void {
    this.audio.pause();
  }

  stop(): void {
    this.audio.pause();
    this.audio.currentTime = 0;
  }

  seek(time: number): void {
    this.audio.currentTime = time;
  }

  setVolume(volume: number): void {
    this.audio.volume = Math.max(0, Math.min(1, volume));
  }

  getCurrentTime(): number {
    return this.audio.currentTime;
  }

  getDuration(): number {
    return this.audio.duration || 0;
  }

  getVolume(): number {
    return this.audio.volume;
  }

  isPlaying(): boolean {
    return !this.audio.paused;
  }

  addEventListener(event: string, handler: EventListener): void {
    this.audio.addEventListener(event, handler);
  }

  removeEventListener(event: string, handler: EventListener): void {
    this.audio.removeEventListener(event, handler);
  }

  destroy(): void {
    this.clearPendingCache();
    this.audio.pause();
    this.audio.src = '';
    this.currentUrl = null;

    // Clean up blob URL
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
  }
}

export default new AudioPlayerService();
